import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useShallow } from 'zustand/react/shallow'
import { v4 as uuidv4 } from 'uuid'
import { ChatBubbleOvalLeftEllipsisIcon } from '@heroicons/react/24/outline'

import { useDocumentState } from '@/state'
import { DocumentTemplate, LETTER_PAGE_HEIGHT } from '../DocumentTemplate'
import { type FlattenedDocumentPage, type View } from '@/lib/api/client'
import { useDocumentPageContextQuery } from '../../queries'
import { useOperationPageTemplateAttributes } from './hooks/useOperationPageTemplateAttributes'
import { useListImages } from '@/services/queries/images'
import { AnnotationsContainer } from '../Annotations/AnnotationsContainer'
import { cleanAndFilterViews } from '@/utils/document_preview'
import { isEmptyOrSpaces } from '@/utils'
import { useStepBOM } from './hooks/useBOM'
import { EmptyDocumentIcon } from '@/components/icons/EmptyDocumentIcon'
import { Button } from '@/components/ui/button'
import { useLocation } from 'wouter'
import { useUpdateDocumentPage } from '@/services/queries/document_pages'
import { useListComments } from '@/services/queries/comments'
import { useDeleteTemplateAttribute } from '@/services/queries/template_attributes'
import { useListTemplateAttributes } from '@/services/hooks/template_attributes'
import { AddSubPageIcon } from '@/components/icons/AddSubPageIcon'
import { RemoveSubPageIcon } from '@/components/icons/RemoveSubPageIcon'
import { AnnotationsMultiSelectContainer } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/AnnotationsMultiSelectContainer'
import { useActiveSubpageFromVirtualizer } from './hooks/useActiveSubpageFromVirtualizer'

const OPERATION_DOCUMENTS_OFFSET = 1000000
const EMPTY_STATE_BANNER_DISMISS_KEY = 'empty_state_banner_dismissed'

export const OperationDocument = ({
  documentPage,
  views,
  versionName,
  createdAt,
  orderNumber,
  isReadOnly,
  isLatestDocumentVersion,
  fullScreen,
  hideCommentsButton,
  parentRef,
}: {
  documentPage: FlattenedDocumentPage
  views: View[]
  versionName: string
  createdAt: string
  orderNumber: number | string
  isReadOnly: boolean
  isLatestDocumentVersion?: boolean
  fullScreen?: boolean
  hideCommentsButton?: boolean
  parentRef?: boolean
}) => {
  const { data: docData } = useDocumentPageContextQuery()
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const currentPageInView = useDocumentState(
    useShallow((state) => state.currentPageInView),
  )
  const isInView = useMemo(
    () => currentPageInView === documentPage.id,
    [currentPageInView, documentPage.id],
  )
  const hasSelectedElement = useDocumentState(
    (state) => state.selectedElement !== null,
  )
  const partsInOperation = useStepBOM({
    documentPageId: documentPage.id,
  })
  const { data: images } = useListImages({
    documentVersionId: documentPage.document_version,
  })

  const {
    notesTemplates,
    labelTemplates,
    imageTemplates,
    viewTemplates,
    genericTables,
    manualPartsTables,
    manualToolsTables,
  } = useOperationPageTemplateAttributes({ documentPageId: documentPage.id })
  const { data: allDocumentTemplates } = useListTemplateAttributes({
    documentPageId: documentPage.id,
  })
  const [isEmptyStateBannerDismissed, setIsEmptyStateBannerDismissed] =
    useState(false)

  const isDocumentThemeEnabled = useMemo(() => {
    return Boolean(documentPage.template_values.created_using_document_theme)
  }, [documentPage.template_values.created_using_document_theme])

  const { mutateAsync: updateOperation } = useUpdateDocumentPage()
  const { mutateAsync: deleteTemplate } = useDeleteTemplateAttribute()

  const { data: documentPageComments } = useListComments({
    documentPageId: documentPage.id,
  })
  const unresolvedComments = documentPageComments?.filter(
    (comment) => comment.comment_status === 'unresolved',
  )
  const setCommentsEnabled = useDocumentState(
    (state) => state.setCommentsEnabled,
  )

  const unselectElement = useCallback(() => {
    setSelectedElement(null)
  }, [setSelectedElement])

  useEffect(() => {
    setIsEmptyStateBannerDismissed(
      !!localStorage.getItem(EMPTY_STATE_BANNER_DISMISS_KEY),
    )
  }, [])

  useEffect(() => {
    if (hasSelectedElement) {
      document.addEventListener('click', unselectElement)
    } else {
      document.removeEventListener('click', unselectElement)
    }

    return () => {
      document.removeEventListener('click', unselectElement)
    }
  }, [unselectElement, hasSelectedElement])

  const imageIds = useMemo(
    () =>
      imageTemplates
        .map((imageTemplate) => imageTemplate.template_values.imageId)
        .filter((id): id is string => Boolean(id)),
    [imageTemplates],
  )
  const imageIdSet = useMemo(() => new Set(imageIds), [imageIds])
  const documentImages = useMemo(
    () =>
      (images || []).filter(
        (image) => Boolean(image.id) && imageIdSet.has(image.id as string),
      ),
    [imageIdSet, images],
  )

  const relativeImages = cleanAndFilterViews({
    viewData: views as View[],
    viewsTemplates: viewTemplates.map((template) => template.template_values),
    documentPageId: documentPage.id as string,
  })

  const absoluteImages = viewTemplates
    .filter((template) => template.template_values.imagePosition === 'absolute')
    .map((template) => {
      const viewId = template.template_values.viewId
      const view = views.find((v) => v.id === viewId)
      return view
    })
    .filter((view) => view !== undefined)
  const nonEmptyRelativeImagesCount = useMemo(
    () => relativeImages.filter((img: any) => img.type !== 'empty').length,
    [relativeImages],
  )

  const documentOrder = useMemo(() => {
    const [whole, ...rest] = orderNumber.toString().split('.')

    if (!rest.length) return Number(whole)

    return Number(
      `${whole}.${rest.reduce((accum, num) => accum + Number(num), 0)}`,
    )
  }, [orderNumber])

  const shouldRenderEmptyDocumentBanner = useMemo(
    () =>
      !isEmptyStateBannerDismissed &&
      Number(orderNumber) === 1 &&
      partsInOperation.length === 0 &&
      manualPartsTables.length === 0 &&
      manualToolsTables.length === 0 &&
      genericTables.length === 0 &&
      absoluteImages.length === 0 &&
      nonEmptyRelativeImagesCount === 0 &&
      notesTemplates.length === 0 &&
      labelTemplates.length === 0 &&
      documentImages.length === 0,
    [
      partsInOperation,
      manualPartsTables,
      manualToolsTables,
      genericTables,
      absoluteImages,
      nonEmptyRelativeImagesCount,
      notesTemplates,
      labelTemplates,
      documentImages,
      isEmptyStateBannerDismissed,
      orderNumber,
    ],
  )

  const shouldRenderNoViewsBanner = useMemo(
    () =>
      !isDocumentThemeEnabled &&
      !isEmptyStateBannerDismissed &&
      Number(orderNumber) === 1 &&
      !shouldRenderEmptyDocumentBanner &&
      absoluteImages.length === 0 &&
      viewTemplates.length === 0 &&
      nonEmptyRelativeImagesCount === 0 &&
      documentImages.length === 0,
    [
      isDocumentThemeEnabled,
      nonEmptyRelativeImagesCount,
      absoluteImages,
      documentImages,
      viewTemplates,
      shouldRenderEmptyDocumentBanner,
      isEmptyStateBannerDismissed,
      orderNumber,
    ],
  )

  const onDismissEmptyStateBanner = useCallback(() => {
    setIsEmptyStateBannerDismissed(true)
    localStorage.setItem(EMPTY_STATE_BANNER_DISMISS_KEY, 'true')
  }, [])

  const subPages: string[] = useMemo(
    () => documentPage?.template_values?.subPages || [documentPage.id],
    [documentPage?.template_values?.subPages, documentPage.id],
  )

  useActiveSubpageFromVirtualizer({
    operationPageId: documentPage.id as string,
    subPageIds: subPages,
    debounceMs: 50,
  })

  const addSubpage = useCallback(
    (subPageId) => {
      void updateOperation({
        documentPageId: documentPage.id as string,
        values: {
          name: documentPage.name,
          template_values: {
            ...documentPage.template_values,
            subPages: [...subPages, subPageId],
          },
          parent_id: documentPage.parent as string,
        },
      })
    },
    [documentPage, subPages, updateOperation],
  )

  const removeSubpage = useCallback(
    async (subPageId) => {
      void updateOperation({
        documentPageId: documentPage.id as string,
        values: {
          name: documentPage.name,
          template_values: {
            ...documentPage.template_values,
            subPages: subPages.filter((id) => id !== subPageId),
          },
          parent_id: documentPage.parent as string,
        },
      })
      const subpageTemplates =
        allDocumentTemplates?.filter(
          (template) => template.template_values.subPageId === subPageId,
        ) || []

      await Promise.all(
        subpageTemplates?.map((template) =>
          deleteTemplate({
            templateAttributeId: template.id as string,
            documentPageId: documentPage.id as string,
          }),
        ),
      )
    },
    [
      documentPage,
      subPages,
      allDocumentTemplates,
      updateOperation,
      deleteTemplate,
    ],
  )

  if (
    !docData ||
    !docData.project ||
    !docData.version ||
    !docData.documentPages
  ) {
    return null
  }

  return (
    <AnnotationsMultiSelectContainer isReadOnly={isReadOnly}>
      <DocumentTemplate
        parentRef={parentRef}
        id={`${documentPage.id}`}
        title={
          isEmptyOrSpaces(documentPage.name)
            ? `Operation ${orderNumber}`
            : documentPage.name
        }
        onEditTitle={(title) => {
          void updateOperation({
            documentPageId: documentPage.id as string,
            values: {
              name: title,
              template_values: documentPage.template_values,
              parent_id: documentPage.parent as string,
            },
          })
        }}
        createdAt={createdAt}
        versionName={versionName}
        isLatestDocumentVersion={isLatestDocumentVersion}
        order={OPERATION_DOCUMENTS_OFFSET + documentOrder}
        testId={`operation-page-${orderNumber}-container`}
        fullScreen={fullScreen}
        isReadOnly={isReadOnly}
        height={subPages.length}
      >
        {isInView && !isReadOnly && (
          <>
            <div
              className="flex flex-col gap-2 absolute top-0 right-0 "
              style={{
                transform: 'translateX(120%)',
              }}
            >
              {!hideCommentsButton && (unresolvedComments?.length || 0) > 0 && (
                <Button
                  variant="ghost"
                  className="!h-auto px-2 p-1 pr-2"
                  onClick={() => setCommentsEnabled(true)}
                >
                  <div className="flex flex-col items-center space-y-2 cursor-pointer relative text-slate-400">
                    <span className="absolute top-[2px] right-[-5px] text-xs">
                      {unresolvedComments?.length}
                    </span>
                    <ChatBubbleOvalLeftEllipsisIcon className="w-6 h-6" />
                  </div>
                </Button>
              )}
              <Button
                className="p-1.5 h-auto rounded-full bg-white shadow-md hover:bg-primary-20/30 border border-gray-100 flex items-center justify-center"
                variant="ghost"
                onClick={() => addSubpage(uuidv4())}
              >
                <AddSubPageIcon className="w-5 h-5" />
              </Button>
            </div>
            {subPages.map((subPageId, i) =>
              i !== 0 ? (
                <div
                  key={subPageId}
                  className="flex flex-col gap-2 absolute top-0 right-0 "
                  style={{
                    transform: 'translateX(120%)',
                    top: i * LETTER_PAGE_HEIGHT,
                  }}
                >
                  <Button
                    className="p-1.5 h-auto rounded-full bg-white shadow-md hover:bg-red-50 border border-gray-100 flex items-center justify-center"
                    variant="ghost"
                    onClick={() => removeSubpage(subPageId)}
                  >
                    <RemoveSubPageIcon className="w-5 h-5" />
                  </Button>
                </div>
              ) : null,
            )}
          </>
        )}
        <AnnotationsContainer
          documentPage={documentPage}
          isReadOnly={isReadOnly}
        >
          {shouldRenderEmptyDocumentBanner && (
            <EmptyDocumentBanner
              isReadOnly={isReadOnly}
              pageId={documentPage.id as string}
              projectId={docData.project.id}
              documentId={docData.doc?.id as string}
              onDismiss={onDismissEmptyStateBanner}
            >
              <div className="text-center text-primary-40 w-[450px]">
                <h4 className="mb-2 mt-1 font-medium text-lg">
                  Link this operation to CAD
                </h4>
                <p className="text-sm">
                  Drag part(s) from the Assembly Tree to the Operation step.
                  Your Parts BOM will update to reflect that link.
                </p>
              </div>
            </EmptyDocumentBanner>
          )}
          {shouldRenderNoViewsBanner ? (
            <EmptyDocumentBanner
              isReadOnly={isReadOnly}
              buttonSize={24}
              pageId={documentPage.id as string}
              projectId={docData.project.id}
              documentId={docData.doc?.id as string}
              onDismiss={onDismissEmptyStateBanner}
            >
              <div className="text-center text-primary-40 w-[450px]">
                <h4 className="mb-2 mt-1 font-normal text-md">
                  Add screenshots from CAD view
                </h4>
              </div>
            </EmptyDocumentBanner>
          ) : null}
        </AnnotationsContainer>
      </DocumentTemplate>
    </AnnotationsMultiSelectContainer>
  )
}

type EmptyDocumentBannerProps = {
  isReadOnly?: boolean
  children: React.ReactNode
  buttonSize?: number
  pageId: string
  projectId: string
  documentId: string
  onDismiss?: () => void
}
const EmptyDocumentBanner = ({
  isReadOnly,
  children,
  buttonSize,
  pageId,
  projectId,
  documentId,
  onDismiss,
}: EmptyDocumentBannerProps) => {
  const [, setLocation] = useLocation()
  const buttonWidthClass = useMemo(
    () => (buttonSize ? `w-${buttonSize}` : ''),
    [buttonSize],
  )
  const buttonHeightClass = useMemo(
    () => (buttonSize ? `h-${buttonSize}` : ''),
    [buttonSize],
  )

  const pageLink = useMemo(
    () => `~/p/${projectId}/document/${documentId}/cad?stepId=${pageId}`,
    [projectId, documentId, pageId],
  )

  return !isReadOnly ? (
    <div
      data-disableprint
      style={{
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
      className="absolute flex flex-col items-center"
    >
      <EmptyDocumentButton
        className={`${buttonWidthClass} ${buttonHeightClass}`}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          setLocation(pageLink)
        }}
      />
      {children}
      <div className="flex items-center content-center mt-2">
        <Button
          variant="outline"
          className="bg-slate-200 text-slate-500 hover:text-slate-400 rounded-2xl px-2 py-0.5"
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  ) : null
}

const EmptyDocumentButton = ({
  className,
  onClick,
}: {
  className?: string
  onClick: MouseEventHandler<HTMLButtonElement>
}) => {
  const [isHovering, setIsHovering] = useState(false)

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className="h-auto hover:bg-transparent"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <EmptyDocumentIcon
        className={className}
        strokeWidth={isHovering ? '2' : '1'}
      />
    </Button>
  )
}
